import { ReactNode, Suspense, useState, useEffect, useRef } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  HardDrive,
  Box,
  PackageOpen,
  LayoutDashboard,
  Book,
  BookOpen,
  Settings,
  Moon,
  Sun,
  Search,
  LogOut,
  ChevronDown,
  Bell,
  Server,
  Check,
  Shield,
  ShieldCheck,
  Database,
  FlaskConical,
  TestTube2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePoolStore } from '@/stores/usePoolStore';
import { config } from '@/config';
import clsx from 'clsx';

interface LayoutProps {
  children?: ReactNode;
}

interface NavItem {
  name: string;
  to: string;
  icon: LucideIcon;
  activeIcon: LucideIcon;
  activeAnimation?: string;
  /** When true, a subtle divider is rendered above this item */
  divider?: boolean;
}

/** Primary nav — flat list, no grouping complexity */
const navigation: NavItem[] = [
  { name: 'Home', to: '/home', icon: LayoutDashboard, activeIcon: LayoutDashboard, activeAnimation: 'animate-icon-bounce' },
  { name: 'Sandboxes', to: '/sandboxes', icon: Box, activeIcon: PackageOpen, activeAnimation: 'animate-icon-pop' },
  { name: 'Images', to: '/images', icon: HardDrive, activeIcon: HardDrive, activeAnimation: 'animate-icon-pop' },
  { name: 'Policies', to: '/policies', icon: Shield, activeIcon: ShieldCheck, activeAnimation: 'animate-icon-pop' },
  { name: 'Volumes', to: '/volumes', icon: Database, activeIcon: Database, activeAnimation: 'animate-icon-pop' },
  { name: 'Docs', to: '/docs', icon: Book, activeIcon: BookOpen, activeAnimation: 'animate-icon-pop', divider: true },
  { name: 'Settings', to: '/settings', icon: Settings, activeIcon: Settings, activeAnimation: 'animate-icon-gear' },
];

export function Layout({ children }: LayoutProps) {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, isLoading: authLoading, logout, roles, betaRoleOverride, isMockingRole } = useAuth();
  const jwtRoleLabel = roles.includes('W365LithiumMgmt.ReadWrite') ? 'ReadWrite' : roles.length > 0 ? 'Read' : null;
  const effectiveRoleLabel = betaRoleOverride ? betaRoleOverride.split('.')[1] : jwtRoleLabel;
  const { pools, selectedPoolIds, fetchPools, togglePool, selectAll, getSelectedPools } =
    usePoolStore();
  const selectedPools = getSelectedPools();
  // Build trigger label for pool selector
  const poolTriggerLabel = (() => {
    if (selectedPools.length === 0) return 'Select pool';
    if (selectedPools.length === pools.length && pools.length > 1) return 'All pools';
    if (selectedPools.length === 1) return selectedPools[0].displayName;
    return `${selectedPools.length} pools`;
  })();

  // Pool dropdown state
  const [poolOpen, setPoolOpen] = useState(false);
  const poolRef = useRef<HTMLDivElement>(null);

  // User menu state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  // Fetch pools after auth is fully settled
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchPools();
    }
  }, [isAuthenticated, authLoading, fetchPools]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (poolRef.current && !poolRef.current.contains(e.target as Node)) {
        setPoolOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setPoolOpen(false);
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="flex h-screen bg-surface-50 dark:bg-mica-bg">
      {/* Skip to main content (a11y) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* ─── Icon Rail ─── */}
      <aside className="icon-rail">
        {/* Brand mark */}
        <div className="flex items-center justify-center h-12 flex-shrink-0">
          <div className="w-8 h-8 rounded-fluent-md bg-brand-500 flex items-center justify-center">
            <span className="text-white text-sm font-bold">W</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col gap-1 py-2">
          {navigation.map(item => (
            <div key={item.name}>
              {item.divider && (
                <div className="mx-3 my-1 border-t border-surface-200 dark:border-white/[0.06]" />
              )}
              <NavLink
                to={item.to}
                end={item.to === '/home'}
                className={({ isActive }) =>
                  clsx('icon-rail-item', isActive && 'icon-rail-item-active')
                }
                title={item.name}
              >
                {({ isActive }) => {
                  const Icon = isActive ? item.activeIcon : item.icon;
                  return (
                    <>
                      <Icon
                        className={clsx(
                          'w-5 h-5',
                          isActive && 'stroke-[2.5]',
                          isActive && item.activeAnimation
                        )}
                      />
                      {!isActive && <span className="icon-rail-label">{item.name}</span>}
                    </>
                  );
                }}
              </NavLink>
            </div>
          ))}
        </nav>

        {/* Footer — theme toggle */}
        <div className="flex flex-col items-center gap-1 py-2 border-t border-surface-200 dark:border-white/[0.06]">
          <button
            onClick={toggleTheme}
            className="icon-rail-item"
            aria-label="Toggle theme"
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* ─── Top Bar ─── */}
      <header className="top-bar">
        {/* App title + pool selector */}
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-sm font-semibold text-surface-900 dark:text-surface-100 whitespace-nowrap">
            {config.appName}
          </h1>

          <div className="w-px h-5 bg-surface-200 dark:bg-white/[0.08]" />

          {/* Pool dropdown */}
          <div ref={poolRef} className="relative">
            <button
              onClick={() => setPoolOpen(!poolOpen)}
              aria-haspopup="listbox"
              aria-expanded={poolOpen}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-fluent-md text-sm transition-all duration-150',
                'text-surface-600 dark:text-surface-300',
                'hover:bg-surface-100 dark:hover:bg-white/[0.06]',
                poolOpen && 'bg-surface-100 dark:bg-white/[0.06]'
              )}
            >
              <Server className="w-3.5 h-3.5 text-brand-500 flex-shrink-0" />
              <span className="font-medium truncate max-w-[160px]">{poolTriggerLabel}</span>
              <ChevronDown
                className={clsx(
                  'w-3.5 h-3.5 text-surface-400 transition-transform duration-150',
                  poolOpen && 'rotate-180'
                )}
              />
            </button>

            {/* Pool dropdown menu */}
            {poolOpen && (
              <div
                className={clsx(
                  'absolute top-full left-0 mt-1 w-56 z-50',
                  'bg-white dark:bg-mica-card rounded-fluent-lg',
                  'border border-surface-200 dark:border-white/[0.08]',
                  'shadow-elevation-8 dark:shadow-none',
                  'py-1 animate-slide-down'
                )}
              >
                {/* Select All option */}
                {pools.length > 1 && (
                  <>
                    <button
                      onClick={() => selectAll()}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                        'hover:bg-surface-50 dark:hover:bg-white/[0.04]',
                        selectedPoolIds.length === pools.length && 'text-brand-500'
                      )}
                    >
                      <div
                        className={clsx(
                          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                          selectedPoolIds.length === pools.length
                            ? 'bg-brand-500 border-brand-500'
                            : 'border-surface-300 dark:border-white/[0.1]'
                        )}
                      >
                        {selectedPoolIds.length === pools.length && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="font-medium">Select All</span>
                    </button>
                    <div className="mx-3 my-1 border-t border-surface-100 dark:border-white/[0.06]" />
                  </>
                )}
                {pools.map(pool => {
                  const isSelected = selectedPoolIds.includes(pool.poolId);
                  return (
                    <button
                      key={pool.poolId}
                      onClick={() => togglePool(pool.poolId)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                        'hover:bg-surface-50 dark:hover:bg-white/[0.04]',
                        isSelected && 'text-brand-500'
                      )}
                    >
                      {/* Checkbox */}
                      <div
                        className={clsx(
                          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                          isSelected
                            ? 'bg-brand-500 border-brand-500'
                            : 'border-surface-300 dark:border-white/[0.1]'
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Server className="w-4 h-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{pool.displayName}</p>
                        <p className="text-xs text-surface-400 truncate">{pool.region}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side: search + notifications + user */}
        <div className="flex items-center gap-1">
          {/* Role badge — always shown when authenticated */}
          {effectiveRoleLabel && (
            <NavLink
              to="/settings"
              title={betaRoleOverride ? 'Beta role override active — click to change' : 'Current JWT role — click to preview as another role'}
              className={clsx(
                'flex items-center gap-1.5 px-2 py-1 rounded-fluent-md text-xs font-semibold border transition-colors',
                betaRoleOverride
                  ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-400/10 border-amber-200 dark:border-amber-400/20 hover:bg-amber-100 dark:hover:bg-amber-400/20'
                  : 'text-surface-500 dark:text-surface-400 bg-surface-100 dark:bg-white/[0.06] border-surface-200 dark:border-white/[0.08] hover:bg-surface-200 dark:hover:bg-white/[0.1]'
              )}
            >
              <FlaskConical className="w-3 h-3" />
              {betaRoleOverride && isMockingRole
                ? `Mocking ${effectiveRoleLabel}`
                : effectiveRoleLabel}
            </NavLink>
          )}

          {/* Search */}
          <button
            className="p-2 rounded-fluent text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-white/[0.06] transition-colors"
            title="Search"
          >
            <Search className="w-4 h-4" />
          </button>

          {/* Notifications placeholder */}
          <button
            className="p-2 rounded-fluent text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-white/[0.06] transition-colors"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
          </button>

          <div className="w-px h-5 bg-surface-200 dark:bg-white/[0.08] mx-1" />

          {/* User avatar + dropdown */}
          {user && (
            <div ref={userRef} className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                className={clsx(
                  'flex items-center gap-2 px-2 py-1.5 rounded-fluent-md transition-colors',
                  'hover:bg-surface-100 dark:hover:bg-white/[0.06]',
                  userMenuOpen && 'bg-surface-100 dark:bg-white/[0.06]'
                )}
              >
                <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[11px] font-semibold">{user.initials}</span>
                </div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-200 hidden sm:block max-w-[120px] truncate">
                  {user.name}
                </span>
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div
                  className={clsx(
                    'absolute top-full right-0 mt-1 w-56 z-50',
                    'bg-white dark:bg-mica-card rounded-fluent-lg',
                    'border border-surface-200 dark:border-white/[0.08]',
                    'shadow-elevation-8 dark:shadow-none',
                    'py-1 animate-slide-down'
                  )}
                >
                  <div className="px-3 py-2 border-b border-surface-100 dark:border-white/[0.06]">
                    <p className="text-sm font-medium text-surface-900 dark:text-surface-100">
                      {user.name}
                    </p>
                    <p className="text-xs text-surface-400 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-surface-600 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-white/[0.04] transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ─── Bottom Tab Bar (mobile) ─── */}
      <nav className="bottom-nav" aria-label="Main navigation">
        {navigation.map(item => (
          <NavLink
            key={item.name}
            to={item.to}
            end={item.to === '/home'}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center justify-center gap-0.5 flex-1 py-2',
                isActive
                  ? 'text-brand-500 dark:text-brand-400'
                  : 'text-surface-500 dark:text-surface-400'
              )
            }
          >
            {({ isActive }) => {
              const Icon = isActive ? item.activeIcon : item.icon;
              return (
                <>
                  <Icon className={clsx('w-5 h-5', isActive && 'stroke-[2.5]', isActive && item.activeAnimation)} />
                  <span className="text-[10px] font-medium">{item.name}</span>
                </>
              );
            }}
          </NavLink>
        ))}
      </nav>

      {/* ─── Main Content ─── */}
      <main
        id="main-content"
        className="flex-1 overflow-auto scrollbar-thin ml-0 sm:ml-16 mt-12 mb-14 sm:mb-0"
      >
        {/* Beta demo banner — always visible, non-dismissable */}
        <div className="sticky top-0 z-40 flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-400/10 border-b border-amber-200 dark:border-amber-400/20">
          <TestTube2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug">
            <span className="font-semibold">Beta UI Demo</span>
            {' — '}Mock data only. No requests reach any real backend service.
            {' '}Visit{' '}
            <NavLink to="/settings" className="underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200">
              Settings
            </NavLink>
            {' '}to override your role and preview what each permission group can see.
          </p>
        </div>

        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            }
          >
            {children || <Outlet />}
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}
